/**
 * useToolSidebar — manages tool recommendations, favorites, filtering,
 * and grouping for the ToolSidebar component.
 */

import React from 'react';

import { ToolRegistry } from '@/engine/ToolRegistry';
import { useToolRecommendations } from '@/hooks/useToolRecommendations';
import type { ISemanticType } from '@/interfaces/ISemanticType';
import type { ITool } from '@/interfaces/ITool';
import { useEditorStore } from '@/store/useEditorStore';
import { useExecutionStore } from '@/store/useExecutionStore';
import { usePresetStore } from '@/store/usePresetStore';
import { useToolBrowserStore } from '@/store/useToolBrowserStore';
import type { FavoriteItem } from '@/types/toolBrowser';

interface UseToolSidebarOptions {
  loadedPipelineOutputType: ISemanticType | null;
}

export function useToolSidebar({ loadedPipelineOutputType }: UseToolSidebarOptions) {
  // Use the same favorites store as ToolCard (useToolBrowserStore)
  const favoriteToolIds = useToolBrowserStore((s) => s.favorites);
  const storeToggleFavorite = useToolBrowserStore((s) => s.toggleFavorite);

  const query = useToolBrowserStore((s) => s.query);
  const input = useEditorStore((s) => s.input);
  const inputType = useEditorStore((s) => s.inputType);
  const pipeline = useEditorStore((s) => s.pipeline);
  const stepResults = useExecutionStore((s) => s.stepResults);
  const pipelineOutput = useEditorStore((s) => s.output);

  const tools = React.useMemo(() => ToolRegistry.getAll(), []);
  const presets = usePresetStore((s) => s.presets);

  /* ---------- derive pipeline output for recommendations ---------- */

  const lastEnabledStepId = React.useMemo(() => {
    for (let i = pipeline.length - 1; i >= 0; i--) {
      const step = pipeline[i];
      if (step?.isEnabled) return step.id;
    }
    return null;
  }, [pipeline]);

  const lastStepResult = React.useMemo(() => {
    if (!lastEnabledStepId) return null;
    for (let i = stepResults.length - 1; i >= 0; i--) {
      const r = stepResults[i];
      if (r.stepId === lastEnabledStepId) return r;
    }
    return null;
  }, [lastEnabledStepId, stepResults]);

  const pipelineDerivedText = lastStepResult?.output ?? pipelineOutput;
  const pipelineDerivedType = lastStepResult?.outputType ?? null;

  /* ---------- recommendations ---------- */

  const recommendationText = pipeline.length > 0 && pipelineDerivedText ? pipelineDerivedText : input;
  const recommendationType = pipeline.length > 0
    ? (pipelineDerivedType ?? loadedPipelineOutputType ?? inputType)
    : inputType;

  const recommendations = useToolRecommendations(recommendationText, recommendationType, {
    maxResults: 50,
    includeGenerators: false,
  });

  const recommendationMap = React.useMemo(() => {
    const map = new Map<string, { score: number; reason: string }>();
    for (const rec of recommendations) {
      map.set(rec.tool.id, { score: rec.score, reason: rec.reason });
    }
    return map;
  }, [recommendations]);

  /* ---------- favorites ---------- */

  const isFavorite = React.useCallback(
    (id: string) => favoriteToolIds.includes(id),
    [favoriteToolIds]
  );

  const toggleFavorite = React.useCallback(
    (toolId: string) => {
      storeToggleFavorite(toolId);
    },
    [storeToggleFavorite]
  );

  const favoriteItems = React.useMemo<FavoriteItem[]>(() => {
    const presetById = new Map(presets.map((p) => [p.id, p] as const));
    return favoriteToolIds
      .map((id): FavoriteItem | null => {
        if (id.startsWith('preset-')) {
          const preset = presetById.get(id);
          if (!preset) return null;
          const tool = ToolRegistry.get(preset.toolId) ?? null;
          return {
            kind: 'preset',
            id,
            presetId: preset.id,
            preset: { id: preset.id, name: preset.name, toolId: preset.toolId, config: preset.config },
            tool,
          };
        }
        const tool = ToolRegistry.get(id);
        if (!tool) return null;
        return { kind: 'tool', id, tool };
      })
      .filter(Boolean) as FavoriteItem[];
  }, [favoriteToolIds, presets]);

  /* ---------- filtered & sorted favorites ---------- */

  const filteredFavoriteItems = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? favoriteItems.filter((item) => {
          if (item.kind === 'tool') {
            const t = item.tool;
            return [t.name, t.description, t.category, ...(t.keywords ?? [])]
              .filter(Boolean)
              .join(' ')
              .toLowerCase()
              .includes(q);
          }
          const preset = item.preset;
          const toolName = item.tool?.name ?? preset.toolId;
          return [preset.name, toolName].filter(Boolean).join(' ').toLowerCase().includes(q);
        })
      : favoriteItems;

    return list.slice().sort((a, b) => {
      const aId = a.kind === 'tool' ? a.tool.id : a.preset.toolId;
      const bId = b.kind === 'tool' ? b.tool.id : b.preset.toolId;
      const aRec = recommendationMap.get(aId);
      const bRec = recommendationMap.get(bId);
      if (aRec && bRec) return bRec.score - aRec.score;
      if (aRec && !bRec) return -1;
      if (!aRec && bRec) return 1;
      const aName = a.kind === 'tool' ? a.tool.name : a.preset.name;
      const bName = b.kind === 'tool' ? b.tool.name : b.preset.name;
      return aName.localeCompare(bName);
    });
  }, [favoriteItems, query, recommendationMap]);

  /* ---------- filtered & sorted tools ---------- */

  const filteredTools = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? tools.filter((t) => {
          return [t.name, t.description, t.category, ...(t.keywords ?? [])]
            .filter(Boolean)
            .join(' ')
            .toLowerCase()
            .includes(q);
        })
      : tools;

    return list.slice().sort((a, b) => {
      const aRec = recommendationMap.get(a.id);
      const bRec = recommendationMap.get(b.id);
      if (aRec && bRec) return bRec.score - aRec.score;
      if (aRec && !bRec) return -1;
      if (!aRec && bRec) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [query, recommendationMap, tools]);

  /* ---------- grouped tools ---------- */

  const groupedTools = React.useMemo(() => {
    const grouped = new Map<string, ITool[]>();

    for (const tool of filteredTools) {
      const category = tool.type === 'visualizer'
        ? 'Visualizers'
        : (tool.category || 'Other');
      if (!grouped.has(category)) grouped.set(category, []);
      grouped.get(category)!.push(tool);
    }

    return new Map(Array.from(grouped.entries()).sort(([a], [b]) => a.localeCompare(b)));
  }, [filteredTools]);

  return {
    groupedTools,
    filteredFavoriteItems,
    recommendationMap,
    isFavorite,
    toggleFavorite,
  };
}
